import React, { useState } from 'react';
import styled from 'styled-components';
import { useLanguage } from '../context/LanguageContext';

const SelectorContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const SelectorButton = styled.button`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 120px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  @media (max-width: 768px) {
    min-width: 100px;
    padding: 6px 10px;
    font-size: 13px;
  }
`;

const FlagIcon = styled.span`
  font-size: 16px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DropdownArrow = styled.div`
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 4px solid currentColor;
  transition: transform 0.2s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 1000;
  min-width: 160px;
  overflow: hidden;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: translateY(${props => props.$isOpen ? '0' : '-10px'});
  transition: all 0.2s ease;
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: ${props => props.$isActive ? '#f8fafc' : 'white'};
  border: none;
  text-align: left;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s ease;

  &:hover {
    background: #f1f5f9;
  }

  &:focus {
    outline: none;
    background: #e2e8f0;
  }
`;

const LanguageFlag = styled.span`
  font-size: 16px;
  width: 20px;
  text-align: center;
`;

const LanguageLabel = styled.span`
  flex: 1;
  font-weight: ${props => props.$isActive ? '600' : '400'};
`;

const ActiveIndicator = styled.div`
  width: 6px;
  height: 6px;
  background: #10b981;
  border-radius: 50%;
  opacity: ${props => props.$show ? 1 : 0};
`;

const LanguageSelector = () => {
  const { language, setLanguage, getSupportedLanguages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  
  const languages = getSupportedLanguages();
  const currentLanguage = languages.find(lang => lang.code === language);

  const handleLanguageSelect = (langCode) => {
    setLanguage(langCode);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('[data-language-selector]')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const getLanguageFlag = (langCode) => {
    const flags = {
      'en': '🇺🇸',
      'pt_br': '🇧🇷'
    };
    return flags[langCode] || '🌐';
  };

  return (
    <SelectorContainer data-language-selector>
      <SelectorButton onClick={toggleDropdown} aria-expanded={isOpen}>
        <FlagIcon>{getLanguageFlag(language)}</FlagIcon>
        <span>{currentLanguage?.label || 'Language'}</span>
        <DropdownArrow $isOpen={isOpen} />
      </SelectorButton>
      
      <DropdownMenu $isOpen={isOpen}>
        {languages.map((lang) => (
          <DropdownItem
            key={lang.code}
            $isActive={lang.isActive}
            onClick={() => handleLanguageSelect(lang.code)}
          >
            <LanguageFlag>{getLanguageFlag(lang.code)}</LanguageFlag>
            <LanguageLabel $isActive={lang.isActive}>
              {lang.label}
            </LanguageLabel>
            <ActiveIndicator $show={lang.isActive} />
          </DropdownItem>
        ))}
      </DropdownMenu>
    </SelectorContainer>
  );
};

export default LanguageSelector; 